import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaWhatsapp } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { AppAPIService } from "../../../../services/AppAPIService";
import store from "../../../../store/store";
import styles from "./styles.module.scss";

const getInitialLawsuitId = () => {
  const params = new URLSearchParams(window.location.search || "");
  return params.get("lawsuitId") || params.get("processo") || params.get("processId") || "";
};

const getWelcomeMessage = (embedded) =>
  embedded
    ? "Olá. Posso consultar seus processos e ajudar a encontrar informações do portal."
    : "Olá. Posso consultar o status cognitivo deste processo e responder perguntas com base nos documentos indexados.";

const buildInitialMessages = (embedded) => [
  {
    id: "welcome",
    type: "assistant",
    text: getWelcomeMessage(embedded),
  },
];

const getAssistantUserStorageId = () => {
  const user = store.getState()?.auth?.user;
  return user?.id || user?.cpfCnpj || user?.email || "anonymous";
};

const readStoredMessages = (storageKey, embedded) => {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(storageKey) || "[]");
    if (Array.isArray(stored) && stored.length) return stored;
  } catch (error) {
    window.sessionStorage.removeItem(storageKey);
  }

  return buildInitialMessages(embedded);
};

const normalizePersistedMessages = (data) => {
  const persistedMessages = Array.isArray(data?.messages) ? data.messages : [];

  return persistedMessages
    .filter((message) => message?.text)
    .map((message) => ({
      id: `persisted-${message.id || message.createdAt || Math.random()}`,
      type: String(message.role || "").toUpperCase() === "USER" ? "user" : "assistant",
      text: message.text,
      status: message.status,
      meta: message.metadata?.handoffRequired ? ["Atendimento humano recomendado"] : undefined,
    }));
};

const PROCESS_NUMBER_PATTERN = /(\b\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}\b|\b\d{20}\b)/g;
const isProcessNumber = (value) => /^(\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}|\d{20})$/.test(value);

const renderProcessNumbers = (text, keyPrefix) =>
  String(text || "")
    .split(PROCESS_NUMBER_PATTERN)
    .map((part, index) => {
      if (isProcessNumber(part)) {
        return <strong key={`${keyPrefix}-process-${index}`}>{part}</strong>;
      }
      return <React.Fragment key={`${keyPrefix}-text-${index}`}>{part}</React.Fragment>;
    });

const renderInlineText = (text) => {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>;
    }

    return <React.Fragment key={`${part}-${index}`}>{renderProcessNumbers(part, `${part}-${index}`)}</React.Fragment>;
  });
};

const MessageText = ({ text }) => {
  const lines = String(text || "").split(/\r?\n/);
  const blocks = [];
  let listType = null;
  let listItems = [];

  const flushList = () => {
    if (!listType || !listItems.length) return;
    const Tag = listType;
    blocks.push(
      <Tag key={`list-${blocks.length}`} className={styles.messageList}>
        {listItems.map((item, index) => (
          <li key={`${item}-${index}`} className={styles.messageListItem}>
            {renderInlineText(item)}
          </li>
        ))}
      </Tag>
    );
    listType = null;
    listItems = [];
  };

  lines.forEach((line) => {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      flushList();
      return;
    }

    const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
    const bulletMatch = trimmedLine.match(/^-\s+(.+)$/);
    const currentListType = numberedMatch ? "ol" : bulletMatch ? "ul" : null;

    if (currentListType) {
      if (listType && listType !== currentListType) flushList();
      listType = currentListType;
      listItems.push(numberedMatch ? numberedMatch[2] : bulletMatch[1]);
      return;
    }

    flushList();
    blocks.push(
      <p key={`paragraph-${blocks.length}`} className={styles.messageParagraph}>
        {renderInlineText(trimmedLine)}
      </p>
    );
  });

  flushList();

  return <div className={styles.messageContent}>{blocks}</div>;
};

const getClientStatusLabel = (status) => {
  if (!status) return "";
  if (status.degraded) return "Consulta usando dados cadastrados";
  if (status.status === "INDEXING") return "Atualizando informações";
  if (status.status === "NOT_INDEXED") return "Preparando informações";
  if (status.status === "ERROR") return "Consulta temporariamente indisponível";
  return "Pronto para responder";
};

const thinkingSteps = [
  "Analisando sua solicitação",
  "Localizando processos relacionados",
  "Verificando documentos, prazos e histórico",
  "Preparando uma resposta clara",
];

const AssistantPage = ({ lawsuitId: fixedLawsuitId = "", embedded = false, processLabel = "" }) => {
  if (!embedded) window.setPageTitle("Assistente Cognitivo");

  const api = useMemo(() => AppAPIService.getInstance(), []);
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const [lawsuitId, setLawsuitId] = useState(fixedLawsuitId || getInitialLawsuitId);
  const [question, setQuestion] = useState("");
  const [status, setStatus] = useState(null);
  const storageKey = useMemo(
    () =>
      `xavier-assistant-chat:v1:${getAssistantUserStorageId()}:${
        embedded ? "global" : fixedLawsuitId || getInitialLawsuitId() || "standalone"
      }`,
    [embedded, fixedLawsuitId]
  );
  const [messages, setMessages] = useState(() => readStoredMessages(storageKey, embedded));
  const [isOpen, setIsOpen] = useState(!embedded);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (embedded && new URLSearchParams(location.search).get("assistant") === "open") {
      setIsOpen(true);
    }
  }, [embedded, location.search]);

  const normalizedLawsuitId = String(lawsuitId || "").replace(/\D/g, "");
  const canConsult = Boolean(normalizedLawsuitId);
  const canAsk = (embedded || canConsult) && question.trim().length > 3;
  const statusSummaryItems = status
    ? [
        getClientStatusLabel(status),
        Number(status.indexedDocuments || 0) > 0 ? `${status.indexedDocuments} documento(s) disponível(is)` : "",
      ].filter(Boolean)
    : [];
  const processUrl = canConsult ? `/admin/processo/${normalizedLawsuitId}/editar` : "/admin/processo";
  const documentsUrl = "/admin/acompanhamentoDocumentos";
  const quickActions = embedded
    ? [
        { label: "Meus processos", question: "Como estão meus processos?" },
        { label: "Documentos", question: "Qual documento falta eu enviar?" },
        { label: "Prazos", question: "Quais prazos eu tenho?" },
        { label: "O que fazer", question: "O que eu preciso fazer agora?" },
        { label: "Urgente", question: "Tenho alguma pendência urgente?" },
        { label: "Atendente", question: "Quero falar com um atendente." },
      ]
    : [
        { label: "Andamento", question: "Como está o andamento?" },
        { label: "Documentos", question: "Quais documentos faltam?" },
        { label: "Prazo", question: "Qual o prazo?" },
        { label: "Urgente", question: "Tenho alguma pendência urgente?" },
        { label: "Valor", question: "Qual o valor do processo?" },
        { label: "Atendente", question: "Quero falar com um atendente." },
      ];

  const request = async (config) => {
    setLoading(true);
    setError("");
    try {
      return await api.makeHttpRequest(config);
    } catch (err) {
      setError("Nao foi possivel consultar o assistente agora.");
      addAssistantMessage({
        text: "Não foi possível consultar o assistente agora. Tente novamente em alguns instantes.",
        status: "ERROR",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const addAssistantMessage = (message) => {
    setMessages((currentMessages) => [
      ...currentMessages,
      {
        id: `${Date.now()}-${Math.random()}`,
        type: "assistant",
        ...message,
      },
    ]);
  };

  const addUserMessage = (text) => {
    setMessages((currentMessages) => [
      ...currentMessages,
      {
        id: `${Date.now()}-${Math.random()}`,
        type: "user",
        text,
      },
    ]);
  };

  useEffect(() => {
    if (!isOpen) return;
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages, loading, isOpen]);

  useEffect(() => {
    window.sessionStorage.setItem(storageKey, JSON.stringify(messages.slice(-40)));
  }, [messages, storageKey]);

  useEffect(() => {
    let isMounted = true;
    const historyUrl = embedded
      ? "/assistant/messages"
      : normalizedLawsuitId
      ? `/assistant/process/${normalizedLawsuitId}/messages`
      : "";

    if (!historyUrl) return undefined;

    api
      .makeHttpRequest({
        method: "GET",
        url: historyUrl,
      })
      .then((data) => {
        if (!isMounted) return;
        const persistedMessages = normalizePersistedMessages(data);
        if (!persistedMessages.length) return;
        setMessages([buildInitialMessages(embedded)[0], ...persistedMessages].slice(-40));
      })
      .catch(() => {
        // O historico local continua disponivel quando o backend estiver indisponivel.
      });

    return () => {
      isMounted = false;
    };
  }, [api, embedded, normalizedLawsuitId, storageKey]);

  useEffect(() => {
    if (!canConsult) return;
    fetchStatus({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedLawsuitId]);

  const clearConversation = async () => {
    const historyUrl = embedded
      ? "/assistant/messages"
      : normalizedLawsuitId
      ? `/assistant/process/${normalizedLawsuitId}/messages`
      : "";

    if (historyUrl) {
      try {
        await api.makeHttpRequest({
          method: "DELETE",
          url: historyUrl,
        });
      } catch (requestError) {
        setError("Não foi possível limpar a conversa agora. Tente novamente.");
        return;
      }
    }

    const initialMessages = buildInitialMessages(embedded);
    setMessages(initialMessages);
    window.sessionStorage.setItem(storageKey, JSON.stringify(initialMessages));
    setError("");
  };

  const fetchStatusForLawsuit = async (targetLawsuitId, { silent = false } = {}) => {
    const targetId = String(targetLawsuitId || "").replace(/\D/g, "");
    if (!targetId) return null;
    if (!silent) addUserMessage("Consultar status cognitivo");
    const data = await request({
      method: "GET",
      url: `/assistant/process/${targetId}/status`,
    });
    if (data) {
      setStatus(data);
      if (!silent) {
        addAssistantMessage({
          text: data.message || "Status cognitivo consultado.",
          status: data.status,
          meta: [
            getClientStatusLabel(data),
            Number(data.indexedDocuments || 0) > 0 ? `${data.indexedDocuments} documento(s) disponível(is)` : "",
          ].filter(Boolean),
        });
      }
    }
    return data;
  };

  const fetchStatus = async ({ silent = false } = {}) => {
    return fetchStatusForLawsuit(normalizedLawsuitId, { silent });
  };

  const reindex = async () => {
    addUserMessage("Atualizar informações do processo");
    const data = await request({
      method: "POST",
      url: `/assistant/process/${normalizedLawsuitId}/reindex`,
      data: { force: true },
    });
    if (data) {
      setStatus(data);
      addAssistantMessage({
          text: data.message || "Atualização das informações registrada.",
        status: data.status,
        meta: [
          getClientStatusLabel(data),
          Number(data.indexedDocuments || 0) > 0 ? `${data.indexedDocuments} documento(s) disponível(is)` : "",
        ].filter(Boolean),
      });
    }
  };

  const ask = async (prompt) => {
    const currentQuestion = String(prompt || question).trim();
    if (currentQuestion.length <= 3 || loading) return;
    addUserMessage(currentQuestion);
    setQuestion("");
    const data = await request({
      method: "POST",
      url: `/assistant/process/${normalizedLawsuitId}/ask`,
      data: { question: currentQuestion },
    });
    if (data) {
      if (data.lawsuitId) {
        const focusedLawsuitId = String(data.lawsuitId);
        setLawsuitId(focusedLawsuitId);
        fetchStatusForLawsuit(focusedLawsuitId, { silent: true });
      }
      addAssistantMessage({
        text: data.answer,
        status: data.status,
        confidence: data.confidence,
        handoffRequired: data.handoffRequired,
        citations: data.citations,
      });
    }
  };

  const askGlobal = async (prompt) => {
    const currentQuestion = String(prompt || question).trim();
    if (currentQuestion.length <= 3 || loading) return;
    addUserMessage(currentQuestion);
    setQuestion("");
    const data = await request({
      method: "POST",
      url: "/assistant/ask",
      data: {
        question: currentQuestion,
        currentLawsuitId: normalizedLawsuitId ? Number(normalizedLawsuitId) : undefined,
        currentPath: window.location.pathname,
      },
    });
    if (data) {
      if (data.lawsuitId) {
        const focusedLawsuitId = String(data.lawsuitId);
        setLawsuitId(focusedLawsuitId);
        fetchStatusForLawsuit(focusedLawsuitId, { silent: true });
      }
      addAssistantMessage({
        text: data.answer,
        status: data.status,
        confidence: data.confidence,
        handoffRequired: data.handoffRequired,
        citations: data.citations,
      });
    }
  };

  const submitPrompt = (prompt) => {
    embedded ? askGlobal(prompt) : ask(prompt);
  };

  const toggleEmbeddedChat = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsOpen((currentValue) => !currentValue);
  };

  const chatContent = (
    <>
      {!embedded && (
        <section className={styles.header}>
          <h1>Assistente Cognitivo</h1>
          <p>Consulta inteligente por processo com respostas baseadas nos documentos disponíveis no portal.</p>
        </section>
      )}

      {!embedded && (
        <section className={styles.summary}>
          <div>
            <strong>Assistente do cliente</strong>
            <span>Respostas orientadas pelos processos, documentos e prazos já cadastrados.</span>
          </div>
          <div className={styles.links}>
            <a href={processUrl}>Processo</a>
            <a href={documentsUrl}>Documentos</a>
            <a href="/admin/assistente/handoffs">Atendimentos</a>
          </div>
        </section>
      )}

      <section className={styles.chatShell}>
        <div className={styles.chatHeader}>
          <div>
            <strong>{embedded ? "Assistente do portal" : "Chat do processo"}</strong>
            <span>
              {embedded
                ? processLabel || "Assistente global"
                : status
                ? getClientStatusLabel(status)
                : "Pronto para consultar"}
            </span>
          </div>
          {embedded && (
            <div className={styles.headerActions}>
              <button type="button" onClick={clearConversation}>
                Limpar
              </button>
              <button type="button" onClick={() => setIsOpen(false)} aria-label="Fechar assistente">
                <FiX aria-hidden="true" />
              </button>
            </div>
          )}
          {!embedded && (
            <div className={styles.headerActions}>
              <button type="button" onClick={clearConversation}>
                Limpar conversa
              </button>
            </div>
          )}
        </div>

        {status && (
          <div className={`${styles.indexStatus} ${status.degraded ? styles.indexStatusDegraded : ""}`}>
            {statusSummaryItems.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        )}

        {!embedded && (
          <div className={styles.quickActions}>
            {quickActions.map((action) => (
              <button type="button" key={action.label} disabled={!canConsult || loading} onClick={() => submitPrompt(action.question)}>
                {action.label}
              </button>
            ))}
          </div>
        )}

        {!embedded && (
          <div className={styles.grid}>
            <label>
              <span>ID do processo</span>
              <input
                value={lawsuitId}
                onChange={(event) => setLawsuitId(event.target.value)}
                placeholder="Digite o ID interno do processo"
              />
            </label>

            <div className={styles.actions}>
              <button type="button" disabled={!canConsult || loading} onClick={fetchStatus}>
                Consultar status
              </button>
              <button type="button" disabled={!canConsult || loading} onClick={reindex}>
                Reindexar
              </button>
            </div>
          </div>
        )}

        {embedded && (
          <div className={styles.quickActions}>
            {quickActions.map((action) => (
              <button type="button" key={action.label} disabled={loading} onClick={() => submitPrompt(action.question)}>
                {action.label}
              </button>
            ))}
          </div>
        )}

        <div className={styles.messages}>
          {messages.map((message) => (
            <div
              key={message.id}
              className={`${styles.messageRow} ${message.type === "user" ? styles.messageRowUser : ""}`}
            >
              <div
                className={`${styles.messageBubble} ${message.type === "user" ? styles.userBubble : ""} ${
                  message.status === "DISABLED" || message.status === "ERROR" ? styles.warningBubble : ""
                }`}
              >
                <MessageText text={message.text} />
                {Array.isArray(message.meta) && message.meta.length > 0 && (
                  <div className={styles.messageMeta}>
                    {message.meta.map((item) => (
                      <span key={item}>{item}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className={styles.messageRow}>
              <div className={`${styles.messageBubble} ${styles.thinkingBubble}`}>
                <strong>Consultando o assistente</strong>
                <ul>
                  {thinkingSteps.map((step, index) => (
                    <li key={step} style={{ animationDelay: `${index * 0.45}s` }}>
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.composer}>
          <textarea
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && canAsk && !loading) {
                event.preventDefault();
                submitPrompt();
              }
            }}
            placeholder={embedded ? "Pergunte sobre seus processos ou informe o número do processo" : "Pergunte algo sobre o processo"}
            rows={2}
          />
          <button type="button" disabled={!canAsk || loading} onClick={() => submitPrompt()}>
            Enviar
          </button>
        </div>
      </section>
    </>
  );

  if (embedded) {
    return ReactDOM.createPortal(
      <>
        <button
          type="button"
          className={`${styles.floatingButton} ${isOpen ? styles.floatingButtonActive : ""}`}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={toggleEmbeddedChat}
          aria-label={isOpen ? "Fechar assistente" : "Abrir assistente pelo WhatsApp"}
          title={isOpen ? "Fechar assistente" : "Assistente pelo WhatsApp"}
        >
          <span>{isOpen ? <FiX aria-hidden="true" /> : <FaWhatsapp aria-hidden="true" />}</span>
        </button>
        {isOpen && (
          <div className={styles.chatWidget}>
            <div className={styles.embeddedContainer}>{chatContent}</div>
          </div>
        )}
      </>,
      document.body
    );
  }

  return (
    <div className={styles.container}>
      {chatContent}
    </div>
  );
};

export default AssistantPage;
